import React, {
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef
} from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Button } from '@sumologic/yama-ds-components';

import { Formik } from 'formik';
import * as Yup from 'yup';

import User from '~/models/User';

import { translate } from '~/modules/helpers/lang';
import { toErrorMessage } from '~/modules/helpers/error';

import Loader from '~/components/Loader/Loader';
import Translate from '~/components/Translate/Translate';

import FormField from '~/containers/FormField/FormField';
import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

import { getUserFields } from '~/pages/GeneralSettings/UserSettings/userFields';

/**
 * Reset password page
 * @return {*}
 * @constructor
 * @author RN
 */
export default function ResetPassword() {
    const [userFields, setUserFields] = useState(null);
    const resetId = useRef(null);

    const stickyAlertContext = useContext(StickyAlertContext);

    /**
     * Show sticky login error
     * @param message
     * @author RN
     */
    const showStickyMessage = message => {
        stickyAlertContext.addStickyAlert({
            status: 'danger',
            title: <Translate message="Reset password" />,
            message: message
        });
    };

    useEffect(() => {
        //const search = useLocation().search;
        resetId.current = findGetParameter('reset');
        User.getPolicyForRecoverPassword(resetId.current)
            .then(response => {
                const passPolicy = response.data;
                let userFields;
                if (passPolicy.password_policy_enabled) {
                    userFields = getUserFields(
                        passPolicy.min_characters,
                        passPolicy.min_uppercase,
                        passPolicy.min_lowercase,
                        passPolicy.min_digits_symbols,
                        true
                    );
                } else {
                    userFields = getUserFields();
                }
                setUserFields(userFields);
            })
            .catch(exception => {
                if (APP_INFO.debug) {
                    console.debug(exception);
                }
                showStickyMessage(
                    toErrorMessage(
                        exception,
                        'An error occurred while retrieving password policy'
                    )
                );
            });
    }, []);

    //RN: find param in URL in plain JS, with react features it doesn't work
    // noinspection NestedFunctionJS
    function findGetParameter(parameterName) {
        let result = null;
        let tmp = [];
        location.search
            .substr(1)
            .split('&')
            .forEach(e => {
                tmp = e.split('=');
                if (parameterName === tmp[0]) {
                    result = decodeURIComponent(tmp[1]);
                }
            });
        return result;
    }

    const getUserField = fieldId => userFields?.find(e => fieldId === e.id);

    const passwordFieldTemplate = getUserField('password');
    const confirmPasswordFieldTemplate = getUserField('confirm_password');

    const passwordFields = [
        {
            ...passwordFieldTemplate,
            label: translate('New password')
        },
        {
            ...confirmPasswordFieldTemplate,
            label: translate('New password confirmation')
        }
    ];

    const createFieldValidationSchema = useCallback(() => {
        const schema = passwordFields.reduce((schema, field) => {
            const val = field.validation;
            if (val) {
                schema[field.id] = val;
            }
            return schema;
        }, {});
        return Yup.object().shape(schema);
    }, [passwordFields]);
    const fieldValidationSchema = createFieldValidationSchema();

    // RN: waiting change password
    const [isChangeLoading, setIsChangeLoading] = useState(false);

    return (
        <div id="reset-change-password" className="reset-change-password">
            <div id="login-page">
                <div className="change-password-container">
                    <div className="header">
                        <span className="section-title">
                            <span className="title">
                                <Translate message="Reset password" />
                            </span>
                        </span>
                        <span className="sub-section-title">
                            <span className="title">
                                <Translate message="Please reset your password before proceeding" />
                            </span>
                        </span>
                    </div>
                    {!userFields && <Loader />}
                    {userFields && (
                        <Formik
                            validationSchema={fieldValidationSchema}
                            initialValues={{}}
                            enableReinitialize
                            validateOnMount
                        >
                            {({ ...formProps }) => {
                                return (
                                    <>
                                        {passwordFields.map((field, index) => {
                                            const {
                                                id,
                                                required,
                                                label,
                                                type
                                            } = field;
                                            let { hint } = field;
                                            const value = formProps.values[id];
                                            const isTouched =
                                                !!formProps.touched[id];
                                            const errors = formProps.errors[id];
                                            const onChange = (id, value) => {
                                                formProps.handleChange({
                                                    target: {
                                                        id,
                                                        value
                                                    }
                                                });
                                            };

                                            const formGroupClasses = [];
                                            if (
                                                passwordFields.length ===
                                                index + 1
                                            ) {
                                                formGroupClasses.push('last');
                                            }

                                            return (
                                                <Form.Group
                                                    key={id}
                                                    className={`form-group ${formGroupClasses.join(
                                                        ' '
                                                    )}`}
                                                >
                                                    <Form.Label
                                                        required={required}
                                                        htmlFor={field.id}
                                                    >
                                                        {label}
                                                    </Form.Label>
                                                    <FormField
                                                        id={id}
                                                        type={type}
                                                        value={value}
                                                        label={label}
                                                        errors={errors}
                                                        isRequired={required}
                                                        isTouched={isTouched}
                                                        onChange={onChange}
                                                        onBlur={
                                                            formProps.handleBlur
                                                        }
                                                    />
                                                    {errors && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors}
                                                        </Form.Control.Feedback>
                                                    )}
                                                    {hint && (
                                                        <Form.Text className="text-muted">
                                                            {hint}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            );
                                        })}
                                        <div className="buttons-container">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                disabled={!formProps.isValid}
                                                onClick={() => {
                                                    setIsChangeLoading(true);
                                                    User.recoverResetPassword(
                                                        resetId.current,
                                                        formProps.values
                                                            .password,
                                                        formProps.values
                                                            .confirm_password
                                                    )
                                                        .then(() => {
                                                            window.location.href = `${APP_URL.django}/ui/`;
                                                        })
                                                        .catch(exception => {
                                                            if (
                                                                APP_INFO.debug
                                                            ) {
                                                                console.debug(
                                                                    exception
                                                                );
                                                            }
                                                            showStickyMessage(
                                                                toErrorMessage(
                                                                    exception,
                                                                    'Reset password error'
                                                                )
                                                            );
                                                        })
                                                        .finally(() => {
                                                            setIsChangeLoading(
                                                                false
                                                            );
                                                        });
                                                }}
                                            >
                                                {isChangeLoading ? (
                                                    <Spinner
                                                        variant="dark"
                                                        animation="border"
                                                        size="sm"
                                                    />
                                                ) : (
                                                    <Translate message="Reset password" />
                                                )}
                                            </Button>
                                        </div>
                                    </>
                                );
                            }}
                        </Formik>
                    )}
                </div>
            </div>
        </div>
    );
}
